<template>
  <div
    v-if="active"
    class="impersonation-chrome"
  >
    <div
      class="banner"
      role="alert"
      aria-live="assertive"
    >
      <span class="banner__label">IMPERSONATING</span>
      <span class="banner__target">{{ targetEmail }}</span>
      <span class="banner__sep">·</span>
      <span class="banner__admin">admin: {{ impersonatorEmail }}</span>
      <span class="banner__sep">·</span>
      <span
        class="banner__reason"
        :title="reason"
      >{{ truncatedReason }}</span>
      <span class="banner__sep">·</span>
      <span class="banner__timer">{{ elapsed }}</span>
      <span class="banner__sep">·</span>
      <span class="banner__hint">Close tab to end</span>
    </div>

    <!-- viewport-edge bars -->
    <div class="viewport-border viewport-border--top" />
    <div class="viewport-border viewport-border--right" />
    <div class="viewport-border viewport-border--bottom" />
    <div class="viewport-border viewport-border--left" />

    <!-- diagonal watermark -->
    <div
      class="watermark"
      aria-hidden="true"
    >
      <div class="watermark__inner">
        {{ watermarkText }}
      </div>
    </div>
  </div>
</template>

<script>
import { IMPERSONATION_FLAG_KEY, IMPERSONATION_WRITE_ACK_KEY, IMPERSONATION_CHANGED_EVENT } from "@/utils/impersonation";

const BANNER_HEIGHT_PX = 48;

export default {
  name: "ImpersonationChrome",
  data() {
    return {
      state: null,
      tickHandle: null,
      now: Date.now(),
      writeGuardInstalled: false,
    };
  },
  computed: {
    active() {
      return !!(this.state && this.state.is_impersonated);
    },
    targetEmail() {
      return (this.state && this.state.target_email) || "unknown";
    },
    impersonatorEmail() {
      return (this.state && this.state.impersonator_email) || "unknown";
    },
    reason() {
      return (this.state && this.state.reason) || "no reason";
    },
    truncatedReason() {
      const r = this.reason;
      return r.length > 40 ? r.slice(0, 38) + "…" : r;
    },
    elapsed() {
      if (!this.state || !this.state.started_at) return "00:00";
      const ms = this.now - this.state.started_at;
      const s = Math.max(0, Math.floor(ms / 1000));
      const mm = String(Math.floor(s / 60)).padStart(2, "0");
      const ss = String(s % 60).padStart(2, "0");
      return mm + ":" + ss;
    },
    watermarkText() {
      const t = this.targetEmail;
      const a = this.impersonatorEmail;
      return "IMPERSONATION · " + a + " AS " + t + " · ";
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(on) {
        this.applyBodyClass(on);
        this.applyFavicon(on);
        this.applyTitle(on);
        if (on) this.installWriteGuard();
      },
    },
    // Catches the case where MagicLoginRedeem set the flag before this
    // component's listener was attached — the post-redeem navigation re-reads.
    $route() {
      this.readFlag();
    },
  },
  mounted() {
    this.readFlag();
    window.addEventListener("storage", this.onStorage);
    window.addEventListener(IMPERSONATION_CHANGED_EVENT, this.readFlag);
    this.tickHandle = setInterval(() => {
      this.now = Date.now();
    }, 1000);
  },
  beforeDestroy() {
    if (this.tickHandle) clearInterval(this.tickHandle);
    window.removeEventListener("storage", this.onStorage);
    window.removeEventListener(IMPERSONATION_CHANGED_EVENT, this.readFlag);
    this.applyBodyClass(false);
    this.applyTitle(false);
  },
  methods: {
    readFlag() {
      try {
        const raw = window.sessionStorage.getItem(IMPERSONATION_FLAG_KEY);
        this.state = raw ? JSON.parse(raw) : null;
      } catch (e) {
        this.state = null;
      }
    },
    onStorage(e) {
      if (e.key === IMPERSONATION_FLAG_KEY) this.readFlag();
    },
    applyBodyClass(on) {
      if (typeof document === "undefined") return;
      document.body.classList.toggle("is-impersonated", on);
      document.body.style.paddingTop = on ? BANNER_HEIGHT_PX + "px" : "";
    },
    applyTitle(on) {
      if (typeof document === "undefined") return;
      const base = document.title.replace(/^\[AS [^\]]+\] - /, "");
      if (on) {
        document.title = "[AS " + this.targetEmail + "] - " + base;
      } else {
        document.title = base;
      }
    },
    // Inline SVG favicon — no extra asset on disk.
    applyFavicon(on) {
      if (typeof document === "undefined") return;
      let link = document.querySelector("link[rel='icon'][data-impersonation]");
      if (on) {
        if (!link) {
          link = document.createElement("link");
          link.rel = "icon";
          link.type = "image/svg+xml";
          link.dataset.impersonation = "1";
          document.head.appendChild(link);
        }
        const svg =
          "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'>" +
          "<rect width='32' height='32' fill='%23dc2626'/>" +
          "<text x='16' y='22' font-size='18' text-anchor='middle' fill='white' font-family='sans-serif' font-weight='bold'>!</text>" +
          "</svg>";
        link.href = "data:image/svg+xml;utf8," + svg;
      } else if (link) {
        link.parentNode && link.parentNode.removeChild(link);
      }
    },
    // Confirm once per tab; banner/watermark/title carry the ongoing reminder.
    installWriteGuard() {
      if (this.writeGuardInstalled || typeof window === "undefined") return;
      this.writeGuardInstalled = true;
      const origSend = window.XMLHttpRequest.prototype.send;
      const origOpen = window.XMLHttpRequest.prototype.open;
      const target = this.targetEmail;
      window.XMLHttpRequest.prototype.open = function (method, url) {
        this.__impersonation_method = (method || "GET").toUpperCase();
        this.__impersonation_url = url || "";
        return origOpen.apply(this, arguments);
      };
      window.XMLHttpRequest.prototype.send = function () {
        const method = this.__impersonation_method;
        const url = this.__impersonation_url || "";
        const writes = method && ["POST", "PUT", "PATCH", "DELETE"].includes(method);
        const flagged = writes && !/(\/admins\/)|(\/auth\/magic-login)/.test(url);
        if (!flagged) return origSend.apply(this, arguments);
        try {
          if (window.sessionStorage.getItem(IMPERSONATION_WRITE_ACK_KEY) === "1") return origSend.apply(this, arguments);
        } catch (_) { /* Intentionally ignored. */ }
        // eslint-disable-next-line no-alert
        const ok = window.confirm(
          "You are about to take action AS " + target +
          ".\n\nFurther writes in this tab will go through silently.\n" +
          "The red banner stays visible until you close the tab.\n\nContinue?",
        );
        if (!ok) {
          this.abort && this.abort();
          return;
        }
        try { window.sessionStorage.setItem(IMPERSONATION_WRITE_ACK_KEY, "1"); } catch (_) { /* Intentionally ignored. */ }
        return origSend.apply(this, arguments);
      };
    },
  },
};
</script>

<style scoped lang="scss">
// Intentional alarm palette — no project token equivalents exist
$imp-red: #dc2626;

.impersonation-chrome {
  position: relative;
  z-index: 2147483647;
}


.banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 48px;
  background: $imp-red;
  color: #fff;
  font: 600 13px/1 -apple-system, BlinkMacSystemFont, "Helvetica Neue", sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
  gap: 8px;
  padding: 0 16px;
  letter-spacing: 0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  z-index: 2147483647;
  pointer-events: none; /* don't intercept clicks */
}
.banner__label {
  background: #fff;
  color: $imp-red;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.08em;
}
.banner__target,
.banner__admin,
.banner__reason,
.banner__timer {
  font-weight: 500;
}
.banner__timer {
  font-variant-numeric: tabular-nums;
  background: rgba(0, 0, 0, 0.18);
  padding: 3px 8px;
  border-radius: 3px;
}
.banner__sep {
  opacity: 0.6;
}
.banner__hint {
  opacity: 0.85;
  font-style: italic;
  font-size: 12px;
}


.viewport-border {
  position: fixed;
  background: $imp-red;
  z-index: 2147483646;
  pointer-events: none;
}
.viewport-border--top {
  top: 0;
  left: 0;
  right: 0;
  height: 8px;
}
.viewport-border--bottom {
  bottom: 0;
  left: 0;
  right: 0;
  height: 8px;
}
.viewport-border--left {
  left: 0;
  top: 0;
  bottom: 0;
  width: 8px;
}
.viewport-border--right {
  right: 0;
  top: 0;
  bottom: 0;
  width: 8px;
}


.watermark {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  overflow: hidden;
  z-index: 2147483645;
}
.watermark__inner {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  transform: rotate(-30deg);
  font: 700 32px/1.4 -apple-system, BlinkMacSystemFont, "Helvetica Neue", sans-serif;
  color: $imp-red;
  opacity: 0.04;
  white-space: pre-wrap;
  word-wrap: break-word;
  letter-spacing: 0.1em;
  text-align: center;
  /* Repeat by overflowing the rotated container with the same string */
  background-image: linear-gradient(transparent, transparent);
}
.watermark__inner::before,
.watermark__inner::after {
  content: attr(data-watermark);
}
</style>

<style>

body.is-impersonated * {
  cursor: crosshair !important;
}
</style>
